import React, { useState } from 'react';
import { useGameStore } from '../../stores/gameStore';
import { useUserStore } from '../../stores/userStore';
import { RARITY_COLORS } from '../../shared/constants';

export function InventoryScreen() {
  const setScreen = useGameStore(s => s.setScreen);
  const user = useUserStore(s => s.user);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  if (!user) return null;

  const tabs = ['all', 'skins', 'titles', 'banners', 'emotes', 'icons', 'crates'];

  const inventoryItems = [
    { id: 'default_skin', name: 'Default', type: 'skin', rarity: 'common', equipped: user.cosmetics.skin === 'default' },
    { id: 'default_title', name: 'Newcomer', type: 'title', rarity: 'common', equipped: user.cosmetics.title === 'Newcomer' },
    { id: 'default_banner', name: 'Standard', type: 'banner', rarity: 'common', equipped: user.cosmetics.banner === 'default' },
    { id: 'default_icon', name: 'Basic', type: 'icon', rarity: 'common', equipped: user.cosmetics.icon === 'default' },
    ...(user.inventory || []).map(item => ({
      id: item.id,
      name: item.itemId,
      type: item.type,
      rarity: 'rare' as const,
      equipped: false,
    })),
  ];

  const filtered = inventoryItems.filter(item => {
    if (activeTab === 'all') return true;
    return item.type === activeTab.slice(0, -1);
  });

  const equipCosmetic = useUserStore(s => s.equipCosmetic);

  return (
    <div className="w-full h-full flex flex-col bg-cf-black">
      <div className="flex items-center justify-between px-6 py-4 border-b border-cf-border">
        <h2 className="text-xl font-bold text-cf-white">Inventory</h2>
        <button onClick={() => setScreen('menu')} className="cf-btn-secondary text-xs">Back</button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-48 border-r border-cf-border p-3 space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
                activeTab === tab
                  ? 'bg-cf-cyan/10 text-cf-cyan border border-cf-cyan/20'
                  : 'text-cf-muted hover:text-cf-text hover:bg-cf-dark'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filtered.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedItem(item.id)}
                className={`cf-panel p-3 text-left transition-all ${
                  item.equipped ? 'border-cf-cyan/50 bg-cf-cyan/5' : 'hover:border-cf-muted/30'
                }`}
              >
                <div className="aspect-square rounded-lg bg-cf-dark mb-2 flex items-center justify-center">
                  <div className="text-2xl opacity-40">
                    {item.type === 'skin' ? '🎨' : item.type === 'title' ? '🏷' : item.type === 'banner' ? '🖼' : '✨'}
                  </div>
                </div>
                <div className="flex items-center gap-1 mb-0.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: RARITY_COLORS[item.rarity] }} />
                  <span className="text-[9px] uppercase font-bold" style={{ color: RARITY_COLORS[item.rarity] }}>
                    {item.rarity}
                  </span>
                </div>
                <h4 className="text-xs font-semibold text-cf-text truncate">{item.name}</h4>
                {item.equipped && (
                  <span className="text-[10px] text-cf-cyan font-bold">EQUIPPED</span>
                )}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center py-16 text-cf-muted">
                No items in this category
              </div>
            )}
          </div>
        </div>

        <div className="w-64 border-l border-cf-border p-4">
          <h3 className="text-sm font-bold text-cf-white mb-3">Equipped</h3>
          <div className="space-y-2">
            {Object.entries(user.cosmetics).filter(([k]) => k !== 'emotes').map(([type, value]) => (
              <div key={type} className="flex items-center justify-between py-2 border-b border-cf-border/30">
                <span className="text-xs text-cf-muted capitalize">{type}</span>
                <span className="text-xs text-cf-text font-medium">{String(value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
